import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InputProductIcon } from 'assets/icons/product.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import { IS_VC_UNIT_CARD_DETAILS_VISIBLE } from 'constants/general';
import { formatRevenueAndPercentage } from 'utils/helpers';

import CustomNodeItem from '../../BusinessStructure/components/CustomNodeItem';

import type { IInputProductNode } from '../interfaces/IInputProductNode';

import styles from '../sass/CustomUnitNode.module.scss';

function InputProductNode({
  name,
  profitCenterIds,
  relianceOnFreeNaturalResources,
  criticalityRating,
  keySupplierTier1OfTotal,
  totalCost,
  isCollapsed,
  isDraft,
  showDraftDeleteButton = true,
  cardsInsideCount,
  onDraftDelete,
  aiPanelSelectionInputName,
  isAIPanelSelectionEnabled,
  isAIPanelSelected,
  isAISessionPreviewSelected,
  onToggleAIPanelSelection,
}: IInputProductNode) {
  const { t } = useTranslation();
  const shouldShowAIPanelCheckbox = Boolean(isAIPanelSelectionEnabled || isAISessionPreviewSelected);
  const isAIPanelCheckboxReadonly = Boolean(isAISessionPreviewSelected && !isAIPanelSelectionEnabled);

  const handleAIPanelSelectionChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.stopPropagation();

    if (isAIPanelCheckboxReadonly) {
      return;
    }

    onToggleAIPanelSelection?.(event.target.checked);
  };

  const customNodeItems = [
    {
      title: t('valueChainMap.profitCenterId'),
      value: profitCenterIds?.join(', ') || '-',
    },
    {
      title: t('valueChainMap.relianceOnFreeNaturalResources'),
      value: relianceOnFreeNaturalResources?.join(', ') || '-',
      isCapitalize: true,
    },
    {
      title: t('valueChainMap.criticalityRating'),
      value: criticalityRating || '-',
      isCapitalize: true,
    },
    {
      title: t('valueChainMap.keySupplierTier1Total'),
      value: formatRevenueAndPercentage({
        actual: totalCost?.amount,
        currency: totalCost?.currency,
        percentage: keySupplierTier1OfTotal?.toString(),
      }),
    },
  ];

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {shouldShowAIPanelCheckbox ? (
            <Checkbox
              name={aiPanelSelectionInputName || `ai_product_select_${name}`}
              value={name}
              checked={Boolean(isAIPanelSelected || isAISessionPreviewSelected)}
              disabled={isAIPanelCheckboxReadonly}
              onChange={handleAIPanelSelectionChange}
              className={clsx(styles.aiPanelCheckbox, {
                [styles.aiPanelCheckboxReadonly]: isAIPanelCheckboxReadonly,
              })}
              size="medium"
            />
          ) : null}
          <InputProductIcon />
          <p className={styles.title}>{name}</p>
        </div>
        <div className={styles.headerRight}>
          {isCollapsed && cardsInsideCount && cardsInsideCount > 0 ? (
            <span className={styles.cardsCount}>{cardsInsideCount}</span>
          ) : null}
          {isDraft && showDraftDeleteButton ? (
            <button
              type="button"
              className={styles.draftDeleteButton}
              aria-label={t('common.delete')}
              onClick={(event) => {
                event.stopPropagation();
                onDraftDelete?.();
              }}
            >
              <TrashIcon className={styles.draftDeleteIcon} />
            </button>
          ) : null}
        </div>
      </div>
      {IS_VC_UNIT_CARD_DETAILS_VISIBLE && !isCollapsed ? (
        <div className={styles.content}>
          {customNodeItems.map(({ title, value, isCapitalize }) => (
            <CustomNodeItem
              key={`customNodeItems_${title}`}
              title={title}
              value={value}
              isCapitalize={isCapitalize}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

export default InputProductNode;
